/**
 * STANDARD BUTTON - Design System 2.0 Component
 *
 * Consistent, beautiful buttons with proper sizing.
 * Replaces all custom Button implementations.
 */

import React from 'react';
import { Button, ButtonProps, SxProps, Theme } from '@mui/material';
import { buttonVariants } from '../../theme';

export type StandardButtonVariant =
  | 'primary'
  | 'secondary'
  | 'gradient'
  | 'gradientSecondary'
  | 'glass'
  | 'outlined'
  | 'text';

export type StandardButtonSize = 'small' | 'medium' | 'large';

interface StandardButtonProps extends Omit<ButtonProps, 'variant' | 'size'> {
  variant?: StandardButtonVariant;
  size?: StandardButtonSize;
  component?: React.ElementType;
  to?: string;
}

const sizeStyles = {
  small: {
    padding: '6px 14px',
    fontSize: '0.8rem',
    minHeight: '32px',
    lineHeight: '1.5',
  },
  medium: {
    padding: '8px 18px',
    fontSize: '0.9rem',
    minHeight: '38px',
    lineHeight: '1.5',
  },
  large: {
    padding: '10px 22px',
    fontSize: '1rem',
    minHeight: '44px',
    lineHeight: '1.5',
  },
};

export const StandardButton = React.forwardRef<HTMLButtonElement, StandardButtonProps>(({
  variant = 'primary',
  size = 'medium',
  children,
  sx,
  ...props
}, ref) => {
  const getVariantSx = () => {
    switch (variant) {
      case 'gradient':
        return buttonVariants.gradientPrimary;
      case 'gradientSecondary':
        return buttonVariants.gradientSecondary;
      case 'glass':
        return buttonVariants.glass;
      case 'outlined':
        return {};  // Use MUI's default outlined styling from theme
      case 'text':
        return {};
      case 'secondary':
        return {};  // Use MUI's default secondary styling from theme
      case 'primary':
      default:
        return {};  // Use MUI's default contained styling from theme
    }
  };

  const variantSx = getVariantSx();
  const sizeSx = sizeStyles[size];

  // Determine MUI variant
  const muiVariant =
    variant === 'outlined' || variant === 'secondary' ? 'outlined' :
    variant === 'text' ? 'text' :
    'contained';

  // Secondary/outlined get color="primary" for themed border/text
  const muiColor =
    variant === 'secondary' || variant === 'outlined' ? 'primary' : undefined;

  return (
    <Button
      ref={ref}
      variant={muiVariant}
      color={muiColor}
      sx={[
        sizeSx,
        variantSx,
        {
          textTransform: 'none',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          '&:focus': {
            outline: 'none',
          },
          '&:focus-visible': {
            outline: '2px solid currentColor',
            outlineOffset: '2px',
          },
        },
        sx
      ] as SxProps<Theme>}
      {...props}
    >
      {children}
    </Button>
  );
});

StandardButton.displayName = 'StandardButton';

export default StandardButton;
